import { useMutation, type UseMutationResult, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

import { orpc } from '@/_shared/lib/orpc';

import {
  SessionNotStartedError,
  type SessionTokens,
  type SignInOutcome,
  signInToLookout,
  type SignupAnswers,
} from '../lib/lookout-session';
import type { LoginRequest } from '../lib/rudderstack-auth-client';

/** A `/login` request. `signup` carries the sign-up page's answers to Lookout, never to config-backend. */
export type SignInRequest = LoginRequest & { signup?: SignupAnswers };

/** The account that a request signs in to: its email, or the sign-up link's token. */
function accountOf(request: LoginRequest): string {
  return 'email' in request ? request.email.trim().toLowerCase() : request.token;
}

/**
 * Signs in at config-backend and opens Lookout; `gcTime: 0` keeps the password out of the cache once the step ends.
 * When config-backend accepted the sign-in but Lookout's session did not start, the next attempt for the same account
 * skips `/login`: it reuses the tokens, or reads the shared session again (ACT2-879). Another email in the form signs
 * in again, so a retry never opens the previous account.
 */
export function useSignIn(
  apiBaseUrl: string,
  returnTo: string | null,
): UseMutationResult<SignInOutcome, Error, SignInRequest> {
  // The sign-in pages render only after the providers probe answered.
  const { data: providers } = useQuery(orpc.auth.providers.queryOptions());
  const sharedSession = providers?.nativeSignIn?.sharedSession === true;
  const pending = useRef<{ account: string; tokens: SessionTokens | null } | null>(null);
  return useMutation({
    mutationFn: ({ signup, ...request }: SignInRequest) => {
      const reuse = pending.current?.account === accountOf(request) ? pending.current.tokens : undefined;
      return signInToLookout({ apiBaseUrl, sharedSession }, request, { returnTo, signup, pending: reuse });
    },
    gcTime: 0,
    onSuccess: (outcome) => {
      pending.current = null;
      if (outcome.kind === 'signed_in') window.location.assign(outcome.redirectTo);
    },
    onError: (error, { signup: _signup, ...request }) => {
      pending.current =
        error instanceof SessionNotStartedError ? { account: accountOf(request), tokens: error.tokens } : null;
    },
  });
}

/** True while signing in, and after, while the browser leaves for Lookout. */
export function isSigningIn(mutation: UseMutationResult<SignInOutcome, Error, SignInRequest>): boolean {
  return mutation.isPending || mutation.data?.kind === 'signed_in';
}
